<template>
  <div data-dusk="automation-description" v-if="!loading && loaded">
    <p>
      Deze automation wordt <b>{{runIntervalText}}</b>uitgevoerd voor <b>{{ searchDescription }}</b>. Aan de hand van de resultaten wordt
      <template v-if="isDataEnrichment">
        de actie <b>{{ automationActionName }}</b> uitgevoerd.
        <template v-if="sendsEmail || createsTask || createsSignal">
          Na het uitvoeren van de actie wordt er {{ takenActionsText }}
        </template>
      </template>
      <template v-else-if="isCampaign">
        een e-mailcampagne verstuurd{{ needsVerificationText }}.
        <template v-if="sendsEmail || createsTask || createsSignal">
          Na het versturen van de campagne wordt er {{ takenActionsText }}
        </template>
      </template>
      <template v-else>
        {{ takenActionsText }}
      </template>
    </p>

    <ul>
      <template v-if="sendsEmail && emailAccount">
        <li>
          <p>
            Er wordt een e-mail verstuurd: vanuit
            <EmailAccountBadgeComponent v-bind:email-account="emailAccount"/>
            met de onderwerpregel <b>{{ emailSubjectDescription }}</b>.
          </p>
        </li>
      </template>
      <template v-else-if="sendsEmail">
        <li>
          <p>
            Er wordt <b>geen</b> e-mail verstuurd: Er is nog geen uitgaande e-mailaccount geconfigureerd.
          </p>
        </li>
      </template>

      <template v-if="createsTask">
        <li>
          <p>
            Er wordt een taak aangemaakt: <b>{{ taskDescription }}</b>
            <template v-if="value.task_assigned_users.length > 0">
              voor {{ value.task_assigned_users.length > 1 ? 'gebruikers' : 'gebruiker' }}
              <UserBadgeGroupComponent style="vertical-align: middle;" :userIds="value.task_assigned_users"/>
            </template>
            <template v-if="value.task_assigned_users.length > 0 && value.task_assigned_user_departments.length > 0">
              en
            </template>
            <template v-if="value.task_assigned_user_departments.length > 0">
              voor {{ value.task_assigned_user_departments.length > 1 ? 'afdelingen' : 'afdeling' }}
              <UserDepartmentBadgeGroupComponent style="vertical-align: middle;" :userDepartmentIds="value.task_assigned_user_departments"/>
            </template>
          </p>
        </li>
      </template>

      <template v-if="createsSignal">
        <li>
          <p>
            Er wordt een signaal aangemaakt: <b>{{ signalDescription }}</b>
            <template v-if="value.signal_assigned_users.length > 0">
              {{ value.signal_assigned_users.length > 1 ? ' voor gebruikers' : ' voor gebruiker' }}
              <UserBadgeGroupComponent style="vertical-align: middle;" :userIds="value.signal_assigned_users"/>
            </template>
            <template v-if="value.signal_assigned_users.length > 0 && value.signal_assigned_user_departments.length > 0">
              en
            </template>
            <template v-if="value.signal_assigned_user_departments.length > 0">
              {{ value.signal_assigned_user_departments.length > 1 ? ' voor afdelingen' : ' voor afdeling' }}
              <UserDepartmentBadgeGroupComponent style="vertical-align: middle;" :userDepartmentIds="value.signal_assigned_user_departments"/>
            </template>
          </p>
        </li>
      </template>
    </ul>

  </div>
</template>

<script>
import {AutomationTypeEnum, RunIntervalEnum} from '../enums'
import { mapActions } from 'vuex'
import automationStore from '~/pages/automation/store'
import searchStore from '~/pages/automation/store/search'
import dynamicExtendingSearchStore from '~/pages/automation/store/dynamic_extending_search'
import UserBadgeGroupComponent from '~/modules/user/components/BadgeGroupComponent'
import UserDepartmentBadgeGroupComponent from '~/modules/user_department/components/BadgeGroupComponent'
import EmailAccountBadgeComponent from '~/modules/email_account/components/BadgeComponent'
import emailAccountStore from '~/modules/email_account/store'
import emailTemplateStore from '~/modules/email_template/store'
import htmlEmailTemplateStore from '~/modules/html_email_template/store'

export default {
  name: 'DescriptionComponent',
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      search: null,
      automationAction: null,
      extendingSearches: null,
      emailAccount: null,
      emailTemplate: null,
      htmlEmailTemplate: null,
      loaded: false,
      loading: false,
    }
  },
  components: {
    UserBadgeGroupComponent,
    UserDepartmentBadgeGroupComponent,
    EmailAccountBadgeComponent,
  },
  watch: {
    'value.search_id': {
      handler () {
        this.loading = true

        this.initializeSearch()
          .then(() => {
            this.loading = false
          })
      },
      deep: true,
    },
    'value.automation_action_id': {
      handler () {
        this.loading = true

        this.initializeAutomationAction()
          .then(() => {
            this.loading = false
          })
      },
      deep: true,
    },
    'value.email_account_id': {
      handler () {
        this.loading = true

        this.initializeEmailAccount()
          .then(() => {
            this.loading = false
          })
      },
      deep: true,
    },
    'value.email_template_id': {
      handler () {
        this.loading = true

        this.initializeEmailTemplate()
          .then(() => {
            this.loading = false
          })
      },
      deep: true,
    },
  },
  created () {
    const SEARCH_STORE_KEY = '$_search'
    // eslint-disable-next-line no-underscore-dangle
    if (!(SEARCH_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(SEARCH_STORE_KEY, searchStore)
    }

    const AUTOMATION_STORE_KEY = '$_automation'
    // eslint-disable-next-line no-underscore-dangle
    if (!(AUTOMATION_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(AUTOMATION_STORE_KEY, automationStore)
    }

    const DYNAMIC_EXTENDING_SEARCH_STORE_KEY = '$_dynamic_extending_search'
    // eslint-disable-next-line no-underscore-dangle
    if (!(DYNAMIC_EXTENDING_SEARCH_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(DYNAMIC_EXTENDING_SEARCH_STORE_KEY, dynamicExtendingSearchStore)
    }

    const EMAIL_ACCOUNT_STORE_KEY = '$_email_account'
    // eslint-disable-next-line no-underscore-dangle
    if (!(EMAIL_ACCOUNT_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(EMAIL_ACCOUNT_STORE_KEY, emailAccountStore)
    }

    const EMAIL_TEMPLATE_STORE_KEY = '$_email_template'
    // eslint-disable-next-line no-underscore-dangle
    if (!(EMAIL_TEMPLATE_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(EMAIL_TEMPLATE_STORE_KEY, emailTemplateStore)
    }

    const HTML_EMAIL_TEMPLATE_STORE_KEY = '$_html_email_template'
    // eslint-disable-next-line no-underscore-dangle
    if (!(HTML_EMAIL_TEMPLATE_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(HTML_EMAIL_TEMPLATE_STORE_KEY, htmlEmailTemplateStore)
    }

    this.loading = true

    Promise.all([
      this.initializeSearch(),
      this.initializeEmailAccount(),
      this.initializeEmailTemplate(),
      this.initializeDynamicExtendingSearches(),
      this.initializeAutomationAction(),
    ])
      .then(() => {
        this.loaded = true
        this.loading = false
      })
  },
  computed: {
    key () {
      if (this.value.id) {
        return 'automation-' + this.value.id
      }

      if (this.globalAutomation.id) {
        return 'global-automation-' + this.globalAutomation.id
      }

      return Math.random()
    },
    globalAutomation () {
      if (this.value.global_automation !== null && this.value.global_automation !== undefined) {
        return this.value.global_automation.data
      }

      return null
    },
    automationActionName () {
      return this.automationAction ? this.automationAction.name : ''
    },
    searchName () {
      if (this.search !== undefined && this.search !== null) {
        return this.search.name
      }

      return 'onbekend'
    },
    extendingSearchesDescription () {
      if (
        this.extendingSearches !== undefined
        && this.extendingSearches !== null
        && this.extendingSearches.length > 0
      ) {
        const description = this.extendingSearches
          .map((extendingSearch) => {
            return extendingSearch.name
          })
          .join(', ')

        return 'met ' + description
      }

      return ''
    },
    searchDescription () {
      return this.searchName + ' ' + this.extendingSearchesDescription
    },
    selectedDynamicSearches () {
      return this.extendingSearches.filter((extendingSearch) => {
        return this.value.dynamic_extending_search_ids?.indexOf(extendingSearch.id) !== -1
      })
    },
    isRecurring () {
      return this.value.is_recurring || false
    },
    createsTask () {
      return this.value.create_task || false
    },
    createsSignal () {
      return this.value.create_signal || false
    },
    sendsEmail () {
      return this.value.send_email || false
    },
    isCampaign () {
      if (this.globalAutomation !== null) {
        return this.globalAutomation.type === AutomationTypeEnum.CAMPAIGN
      }

      return this.value.type === AutomationTypeEnum.CAMPAIGN
    },
    isSelection () {
      if (this.globalAutomation !== null) {
        return this.globalAutomation.type === AutomationTypeEnum.SELECTION
      }

      return this.value.type === AutomationTypeEnum.SELECTION
    },
    isDataEnrichment () {
      if (this.globalAutomation !== null) {
        return this.globalAutomation.type === AutomationTypeEnum.DATA_ENRICHMENT
      }

      return this.value.type === AutomationTypeEnum.DATA_ENRICHMENT
    },
    emailSubjectDescription () {
      return this.emailTemplate !== null ? this.emailTemplate.title : 'onbekend'
    },
    taskDescription () {
      return this.value.task_title || 'onbekend'
    },
    signalDescription () {
      return this.value.signal_title || 'onbekend'
    },
    needsVerificationText(){
      return this.value.needs_verification ? ' (er is handmatige goedkeuring nodig voordat deze campagne wordt verzonden)' : '';
    },
    takenActionsText(){
      const emailText = this.sendsEmail ? 'een e-mail verstuurt' : '';
      const connectionText = this.sendsEmail && this.createsTask && this.createsSignal ?
            ', '
          : this.sendsEmail && (this.createsTask || this.createsSignal) ?
            ' en '
          : '';
      const taskText = this.createsTask ? 'een taak' : '';
      const signalText = this.createsSignal ? 'een signaal' : '';
      const taskSignalSuffix = this.createsTask && this.createsSignal ? ' en ' : '';
      const taskSignalEnd = this.createsTask || this.createsSignal ? ' aangemaakt.' : '';
      return `${emailText}${connectionText}${taskText}${taskSignalSuffix}${signalText}${taskSignalEnd}`;
    },
    runIntervalText() {
      if (this.value.run_interval === RunIntervalEnum.DAILY) {
        return 'dagelijks ';
      } else if (this.value.run_interval === RunIntervalEnum.ONCE) {
        return 'eenmalig ';
      } else if (this.value.run_interval === RunIntervalEnum.WEEKLY) {
        return 'wekelijks ';
      } else if (this.value.run_interval === RunIntervalEnum.MONTHLY) {
        return 'maandelijks ';
      } else if (this.value.run_interval === RunIntervalEnum.QUARTERLY) {
        return 'elk kwartaal ';
      } else if (this.value.run_interval === RunIntervalEnum.YEARLY) {
        return 'jaarlijks ';
      }
    }
  },
  methods: {
    ...mapActions({
      getAutomationActionById: '$_automation/getAutomationActionById',
      getEmailAccountById: '$_email_account/getEmailAccountById',
      getEmailTemplateById: '$_email_template/getEmailTemplateById',
      getHtmlEmailTemplateById: '$_html_email_template/getHtmlEmailTemplateById',
      getSearchById: '$_search/getSearchById',
      getDynamicExtendingSearchesByIds: '$_dynamic_extending_search/getDynamicExtendingSearchesByIds',
    }),
    initializeEmailAccount () {
      if (
        this.value.email_account !== null
        && this.value.email_account !== undefined
        && this.value.email_account.data !== null
        && this.value.email_account.data !== undefined
      ) {
        this.emailAccount = this.value.email_account.data

        return Promise.resolve()
      }

      if (
        this.value.email_account_id !== null
        && this.value.email_account_id !== undefined
      ) {
        return this.getEmailAccountById(this.value.email_account_id)
          .then((emailAccount) => {
            this.emailAccount = emailAccount
          })
      }

      return Promise.resolve()
    },
    initializeEmailTemplate () {
      if (
        this.value !== null
        && this.value !== undefined
        && this.value.email_template !== null
        && this.value.email_template !== undefined
        && this.value.email_template.data !== null
        && this.value.email_template.data !== undefined
      ) {
        this.emailTemplate = this.value.email_template.data

        return Promise.resolve()
      }

      if (
        this.value !== null
        && this.value !== undefined
        && this.value.email_template_id !== null
        && this.value.email_template_id !== undefined
      ) {
        return this.getEmailTemplateById(this.value.email_template_id)
          .then((emailTemplate) => {
            this.emailTemplate = emailTemplate
          })
      }

      if (
        this.value !== null
        && this.value !== undefined
        && this.value.global_automation !== null
        && this.value.global_automation !== undefined
        && this.value.global_automation.data !== null
        && this.value.global_automation.data !== undefined
        && this.value.global_automation.data.global_email_template !== null
        && this.value.global_automation.data.global_email_template !== undefined
      ) {
        this.emailTemplate = this.value.global_automation.data.global_email_template.data

        return Promise.resolve()
      }

      return Promise.resolve()
    },
    initializeHtmlEmailTemplate () {
      if (
        this.value !== null
        && this.value !== undefined
        && this.value.html_email_template !== null
        && this.value.html_email_template !== undefined
        && this.value.html_email_template.data !== null
        && this.value.html_email_template.data !== undefined
      ) {
        this.htmlEmailTemplate = this.value.html_email_template.data

        return Promise.resolve()
      }

      if (
        this.value !== null
        && this.value !== undefined
        && this.value.html_email_template_id !== null
        && this.value.html_email_template_id !== undefined
      ) {
        return this.getHtmlEmailTemplateById(this.value.html_email_template_id)
          .then((htmlEmailTemplate) => {
            this.htmlEmailTemplate = htmlEmailTemplate
          })
      }

      if (
        this.value !== null
        && this.value !== undefined
        && this.value.global_automation !== null
        && this.value.global_automation !== undefined
        && this.value.global_automation.data !== null
        && this.value.global_automation.data !== undefined
        && this.value.global_automation.data.global_html_email_template !== null
        && this.value.global_automation.data.global_html_email_template !== undefined
      ) {
        this.htmlEmailTemplate = this.value.global_automation.data.global_html_email_template.data

        return Promise.resolve()
      }

      return Promise.resolve()
    },
    initializeSearch () {
      if (
        this.value.search !== null
        && this.value.search !== undefined
        && this.value.search.data !== null
        && this.value.search.data !== undefined
      ) {
        this.search = this.value.search.data

        return Promise.resolve()
      }

      // check for eager loaded relation in global automation configuration
      if (
        this.value.global_automation !== null
        && this.value.global_automation !== undefined
        && this.value.global_automation.data !== null
        && this.value.global_automation.data !== undefined
        && this.value.global_automation.data.global_search !== null
        && this.value.global_automation.data.global_search !== undefined
        && this.value.global_automation.data.global_search.data !== null
        && this.value.global_automation.data.global_search.data !== undefined
      ) {
        this.search = this.value.global_automation.data.global_search.data

        return Promise.resolve()
      }

      if (
        this.value.search_id !== null
        && this.value.search_id !== undefined
      ) {
        return this.getSearchById(this.value.search_id)
          .then((search) => {
            this.search = search
          })
          .catch((error) => {
            this.$notification.error({
              message: 'Er is een fout opgetreden bij het ophalen van de selectie.',
              description: error.message,
            })
          })
      }

      // load search by id in global automation configuration
      if (
        this.value.global_automation !== null
        && this.value.global_automation !== undefined
        && this.value.global_automation.data !== null
        && this.value.global_automation.data !== undefined
        && this.value.global_automation.data.global_search_id !== null
        && this.value.global_automation.data.global_search_id !== undefined
      ) {
        return this.getSearchById(this.value.global_automation.data.global_search_id)
          .then((search) => {
            this.search = search
          })
          .catch((error) => {
            this.$notification.error({
              message: 'Er is een fout opgetreden bij het ophalen van de selectie.',
              description: error.message,
            })
          })
      }

      return Promise.resolve()
    },
    initializeDynamicExtendingSearches () {
      if (
        this.value.extending_searches !== null
        && this.value.extending_searches !== undefined
        && this.value.extending_searches.data !== null
        && this.value.extending_searches.data !== undefined
      ) {
        this.extendingSearches = this.value.extending_searches.data

        return Promise.resolve()
      }

      if (
        this.value.dynamic_extending_search_ids !== null
        && this.value.dynamic_extending_search_ids !== undefined
        && this.value.dynamic_extending_search_ids.length > 0
      ) {
        return this.getDynamicExtendingSearchesByIds(this.value.dynamic_extending_search_ids)
          .then((extendingSearches) => {
            this.extendingSearches = extendingSearches
          })
          .catch((error) => {
            this.$notification.error({
              message: 'Er is een fout opgetreden bij het ophalen van de uitbreidende selecties.',
              description: error.message,
            })
          })
      }

      return Promise.resolve()
    },
    initializeAutomationAction () {
      // Check for eager loaded relation in automation
      if (
        this.value.automation_action !== null
        && this.value.automation_action !== undefined
        && this.value.automation_action.data !== null
        && this.value.automation_action.data !== undefined
      ) {
        this.automationAction = this.value.automation_action.data

        return Promise.resolve()
      }

      // check for eager loaded relation in global automation configuration
      if (
        this.value.global_automation !== null
        && this.value.global_automation !== undefined
        && this.value.global_automation.data !== null
        && this.value.global_automation.data !== undefined
        && this.value.global_automation.data.automation_action !== null
        && this.value.global_automation.data.automation_action !== undefined
        && this.value.global_automation.data.automation_action.data !== null
        && this.value.global_automation.data.automation_action.data !== undefined
      ) {
        this.automationAction = this.value.global_automation.data.automation_action.data

        return Promise.resolve()
      }

      // load by action id in automation
      if (
        this.value.automation_action_id !== null
        && this.value.automation_action_id !== undefined
      ) {
        return this.getAutomationActionById(this.value.automation_action_id)
          .then((automationAction) => {
            this.automationAction = automationAction
          })
          .catch((error) => {
            this.$notification.error({
              message: 'Er is een fout opgetreden bij het ophalen van de actie.',
              description: error.message,
            })
          })
      }

      // load by action id in global automation configuration
      if (
        this.value.global_automation !== null
        && this.value.global_automation !== undefined
        && this.value.global_automation.data !== null
        && this.value.global_automation.data !== undefined
        && this.value.global_automation.data.automation_action_id !== null
        && this.value.global_automation.data.automation_action_id !== undefined
      ) {
        return this.getAutomationActionById(this.value.global_automation.data.automation_action_id)
          .then((automationAction) => {
            this.automationAction = automationAction
          })
          .catch((error) => {
            this.$notification.error({
              message: 'Er is een fout opgetreden bij het ophalen van de actie.',
              description: error.message,
            })
          })
      }

      return Promise.resolve()
    },
  },
}
</script>


<style scoped lang="scss">
@import '~@/_default.scss';

ul {
  list-style-type: disc;
  padding-left: $spacing-lg;
  color: $color-primary;

  li p {
    margin-bottom: 0;
  }
}
</style>
